import { useEffect, useRef, useCallback } from 'react';

const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;
const CHECK_INTERVAL_MS = 30 * 1000;
const THROTTLE_MS = 10 * 1000;
export const INACTIVITY_STORAGE_KEY = 'lastActivityTimestamp';

const updateLastActivity = () => {
  localStorage.setItem(INACTIVITY_STORAGE_KEY, Date.now().toString());
};

const getLastActivity = (): number => {
  const stored = localStorage.getItem(INACTIVITY_STORAGE_KEY);
  return stored ? parseInt(stored, 10) : Date.now();
};

export const useInactivityTimeout = (
  enabled: boolean,
  onTimeout: () => void,
) => {
  const lastThrottledUpdate = useRef(0);
  const hasTimedOut = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const checkTimeout = useCallback(() => {
    if (hasTimedOut.current) return;
    const elapsed = Date.now() - getLastActivity();
    if (elapsed >= INACTIVITY_TIMEOUT_MS) {
      hasTimedOut.current = true;
      onTimeoutRef.current();
    }
  }, []);

  const handleUserActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastThrottledUpdate.current >= THROTTLE_MS) {
      lastThrottledUpdate.current = now;
      updateLastActivity();
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      hasTimedOut.current = false;
      return;
    }

    // Only set a fresh timestamp if none exists (i.e. fresh login).
    // On page reload / returning after closing the browser, we preserve
    // the existing timestamp so the elapsed-time check works correctly.
    if (!localStorage.getItem(INACTIVITY_STORAGE_KEY)) {
      updateLastActivity();
    }

    // Check immediately — catches timeout that elapsed while the browser
    // was closed or the user was away.
    checkTimeout();
    if (hasTimedOut.current) return;

    const activityEvents = [
      'mousemove',
      'keydown',
      'mousedown',
      'touchstart',
      'scroll',
      'click',
    ] as const;

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    // Periodic check as a safety net while the tab is open.
    const intervalId = setInterval(checkTimeout, CHECK_INTERVAL_MS);

    // When the user returns to the tab, check immediately.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkTimeout();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleUserActivity);
      });
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, handleUserActivity, checkTimeout]);
};
